import { Signal, inject } from '@angular/core';

import {
  QueryClient,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { NomObjectClass } from 'src/app/core/constants';
import { Alert } from 'src/app/core/models/alert.models';
import { AlertData } from 'src/app/core/services/alert.service';

import { AlertApiService } from '../services/alert-api.service';
import { UserApiService } from '../services/user-api.service';

/**
 * A feed's alerts: the list read plus the four writes that patch it in place.
 *
 * The sidebar badge count is **not** here. `FeedState.alertsCount` is seeded from
 * `feed.count_alerts` and adjusted by ±1 on the create/delete broadcasts, so it is
 * right before anyone opens the panel - which a list query cannot do. Call sites
 * must therefore keep dispatching `createAlertSuccess` / `deleteAlertSuccess`.
 */

const ALERTS_ROOT_KEY = ['alert', 'list'] as const;

export const feedAlertsQueryKey = (feedId: string | null) =>
  [...ALERTS_ROOT_KEY, { objectId: feedId ?? null }] as const;

/** Every mounted alerts list, whichever object it belongs to. */
const allAlertListsFilter = { queryKey: ALERTS_ROOT_KEY };

const TRIGGERED_ALERTS_ROOT_KEY = ['alert', 'triggered'] as const;

export interface TriggeredAlertsScope {
  userId: string;
  companyId: string;
}

/**
 * The user's triggered alerts (GET /user/:id/alerts).
 *
 * **A key root of its own, not a third `['alert','list']` entry**, even though both
 * hold `Alert[]`. Sharing the root would hand this list every patch the feed lists
 * take, and two of those are wrong here: `createAlert` appends an alert that has
 * not triggered yet, and `editAlert` substitutes the editor's response for the row
 * - which carries no `last_triggered_timestamp` / `status` / `feed_title`, the
 * three fields this list renders. Delete and toggle *are* shared, through
 * `patchEveryAlertList`.
 *
 * `companyId` is a key term so a company switch re-keys the entry: a live observer
 * refetches on a key change, not on a cache clear.
 */
export const triggeredAlertsQueryKey = ({
  userId,
  companyId,
}: TriggeredAlertsScope) =>
  [...TRIGGERED_ALERTS_ROOT_KEY, { userId, companyId }] as const;

/**
 * A different feed is a different entry, so re-opening the panel refetches. The
 * cache is the state here, so no background refetch may land on top of a patch.
 */
export function injectFeedAlertsQuery(feedId: Signal<string | null>) {
  const alertApi = inject(AlertApiService);

  return injectQuery(() => ({
    queryKey: feedAlertsQueryKey(feedId()),
    queryFn: () =>
      firstValueFrom(alertApi.listAlerts(NomObjectClass.FEED, feedId()!)).then(
        (alerts) => alerts ?? [],
      ),
    enabled: !!feedId(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}

export interface CreateAlertInput {
  feedId: string;
  alert: AlertData;
}

export function injectCreateAlertMutation() {
  const alertApi = inject(AlertApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ feedId, alert }: CreateAlertInput) =>
      firstValueFrom(alertApi.createAlert(feedId, alert)),
    onSuccess: (alert, { feedId }) => {
      queryClient.setQueryData<Alert[]>(
        feedAlertsQueryKey(feedId),
        (current) => (current ? [...current, alert] : [alert]),
      );
      toast.showSuccess(
        translate.instant('feed.toasts.alert-created-successfully'),
      );
    },
  }));
}

export interface EditAlertInput {
  alertId: string;
  alert: AlertData;
}

/**
 * Patches every mounted list rather than one feed's: the editor knows the alert
 * id, not necessarily which list is on screen.
 */
export function injectEditAlertMutation() {
  const alertApi = inject(AlertApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ alertId, alert }: EditAlertInput) =>
      firstValueFrom(alertApi.editAlert(alertId, alert)),
    onSuccess: (saved) => {
      patchAlertLists(queryClient, (alerts) =>
        alerts.map((alert) => (alert.id === saved.id ? saved : alert)),
      );
      toast.showSuccess(
        translate.instant('feed.toasts.alert-edited-successfully'),
      );
    },
  }));
}

/** No toast on either outcome, so a failed delete is silent today. */
export function injectDeleteAlertMutation() {
  const alertApi = inject(AlertApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (alertId: string) =>
      firstValueFrom(alertApi.deleteAlert(alertId)),
    onSuccess: (_result, alertId) => {
      patchEveryAlertList(queryClient, (alerts) =>
        alerts.filter((alert) => alert.id !== alertId),
      );
    },
  }));
}

/**
 * The mode flip is derived client-side because the endpoint answers with no usable
 * body.
 */
export function injectToggleAlertModeMutation() {
  const alertApi = inject(AlertApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (alertId: string) =>
      firstValueFrom(alertApi.toggleAlertMode(alertId)),
    onSuccess: (_result, alertId) => {
      patchEveryAlertList(queryClient, (alerts) =>
        alerts.map((alert) =>
          alert.id === alertId
            ? {
                ...alert,
                mode:
                  alert.mode?.id == 1
                    ? { id: 2, title: 'Paused' }
                    : { id: 1, title: 'Running' },
              }
            : alert,
        ),
      );
      toast.showSuccess(
        translate.instant('feed.toasts.alert-mode-changed-successfully'),
      );
    },
    onError: () => {
      toast.showError(
        translate.instant('feed.toasts.alert-mode-change-failed'),
      );
    },
  }));
}

/**
 * Apply a patch to every cached *feed* alerts list. Delete and toggle are
 * reachable from hosts that carry no feed id (the alerts center, the
 * object-card dropdown), so they cannot target one entry by key.
 */
const patchAlertLists = (
  queryClient: QueryClient,
  patch: (alerts: Alert[]) => Alert[],
): void => {
  queryClient.setQueriesData<Alert[]>(allAlertListsFilter, (current) =>
    current ? patch(current) : current,
  );
};

/**
 * Apply a patch to the triggered-alerts list too. A partial key sweep: call sites
 * know an alert id and nothing about which user or company the list belongs to.
 */
const patchTriggeredAlerts = (
  queryClient: QueryClient,
  patch: (alerts: Alert[]) => Alert[],
): void => {
  queryClient.setQueriesData<Alert[]>(
    { queryKey: TRIGGERED_ALERTS_ROOT_KEY },
    (current) => (current ? patch(current) : current),
  );
};

/** Both roots - for the two writes that mean the same thing in either list. */
const patchEveryAlertList = (
  queryClient: QueryClient,
  patch: (alerts: Alert[]) => Alert[],
): void => {
  patchAlertLists(queryClient, patch);
  patchTriggeredAlerts(queryClient, patch);
};

/**
 * The user's triggered alerts - one fetch per session, whichever of the two
 * consumers mounts first.
 *
 * `staleTime` and `gcTime` are both `Infinity` because the per-row `expanded` flag
 * lives *in* this cache: a background refetch would silently collapse rows the user
 * opened, and both consumers unmount (the dropdown when it closes, the alerts
 * center on navigation).
 */
export function injectTriggeredAlertsQuery(
  scope: Signal<TriggeredAlertsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? triggeredAlertsQueryKey(current)
        : ([...TRIGGERED_ALERTS_ROOT_KEY, null] as const),
      queryFn: () =>
        firstValueFrom(userApi.getTriggeredAlerts(current!.userId)).then(
          (alerts) => alerts ?? [],
        ),
      enabled: !!current,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * The dropdown's show-more/show-less row state, written into the cache rather than
 * beside it: the panel is destroyed when it closes, so a component-side signal
 * would lose it between opens.
 */
export function toggleTriggeredAlertExpanded(
  queryClient: QueryClient,
  alertId: string,
): void {
  patchTriggeredAlerts(queryClient, (alerts) =>
    alerts.map((alert) =>
      alert.id === alertId ? { ...alert, expanded: !alert.expanded } : alert,
    ),
  );
}

/**
 * The header's triggered-alert badge. One writer only, unlike its notification
 * counterpart: nothing on the socket touches it and opening the alerts panel does
 * not clear it. `Infinity` on both times all the same, so re-entering Presentation
 * Mode - which unmounts the header - does not re-request it.
 */
export function injectUserAlertsCountQuery(
  scope: Signal<TriggeredAlertsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? (['alert', 'triggered_count', current] as const)
        : (['alert', 'triggered_count', null] as const),
      queryFn: () => firstValueFrom(userApi.getAlertsCount(current!.userId)),
      enabled: !!current,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}
